import { useInboxContext } from '@/contexts/inbox-provider';
import type { Deal } from '@/models/types';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useDealsForHub } from './use-deals-selectors';
import {
  getInformativePathname,
  getUninformativePathname,
} from './use-redirect-to-informative-deal-url';

export const urlWithQueryParams = (
  url: string,
  queryParams: Record<string, string>,
) => {
  const params = new URLSearchParams(queryParams);
  const paramsString = params.toString();

  return `${url}${paramsString ? `?${paramsString}` : ''}`;
};

export const urlWithQueryString = ({
  url,
  queryString,
  addQuestionMark = false,
}: {
  url: string;
  queryString: string;
  addQuestionMark?: boolean;
}) => {
  return `${url}${addQuestionMark ? '?' : ''}${queryString}`;
};

export const urlWithFragment = (url: string, fragment?: string | undefined) => {
  return `${url}${fragment ? `#${fragment}` : ''}`;
};

// IMPORTANT UX NOTE: Do not go to a deal page without using this or extending this functionality
// otherwise the inbox deal view will break!!!
const useGoToDeal = () => {
  const navigate = useNavigate();
  const { setIsInboxView, setIsFromMobileInbox } = useInboxContext();
  const deals = useDealsForHub();
  const { hub: hubPath } = useParams({ strict: false });

  const goToDeal = useCallback(
    ({
      dealShortId,
      isForInbox = false,
      isFromMobileInbox = false,
      // urlFragment = "",
      queryParams = {},
      openInNewTab = false,
    }: {
      dealShortId: Deal['short_id'];
      isForInbox?: boolean;
      isFromMobileInbox?: boolean;
      // urlFragment?: string;
      queryParams?: Record<string, string>;
      openInNewTab?: boolean;
    }) => {
      // UX NOTE: Set the session storage so we can get the correct view
      if (isForInbox) {
        setIsInboxView(true);
      } else {
        setIsInboxView(false);
      }

      setIsFromMobileInbox(isFromMobileInbox);

      const deal = deals.find((deal) => deal.short_id === dealShortId);
      const dealHubPath = deal?.hub_path_identifier;
      const uninformativePathname = getUninformativePathname(
        dealShortId,
        dealHubPath,
      );

      if (deal) {
        let newPathname = uninformativePathname;

        if (deal.primary_customer && deal.vehicle_listing) {
          const informativePathname = getInformativePathname({
            primaryCustomerFullName: deal.primary_customer.full_name,
            vehicle: deal.vehicle_listing,
            primarySalesRep: deal.primary_sales_rep,
            secondarySalesRep: deal.secondary_sales_rep,
            shortId: dealShortId,
            fallbackToUninformativePathname: true,
            overrideHubPathIdentifier: dealHubPath,
          });

          newPathname = informativePathname || uninformativePathname;
        }

        const newUrl = urlWithQueryParams(
          urlWithFragment(newPathname, undefined),
          queryParams,
        );

        if (openInNewTab) {
          window.open(newUrl, '_blank');
          return;
        }

        if (hubPath === newPathname) {
          return;
        }

        navigate({ to: newUrl });
      } else {
        const newPathname = uninformativePathname;

        if (hubPath === newPathname) {
          return;
        }

        const newUrl = urlWithQueryParams(uninformativePathname, queryParams);
        navigate({ to: newUrl });
      }
    },
    [setIsInboxView, setIsFromMobileInbox, navigate, deals, hubPath],
  );

  return { goToDeal };
};

export default useGoToDeal;
